import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Briefcase, Users, TrendingUp, Heart, GraduationCap, Award, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function Careers() {
  const [searchQuery, setSearchQuery] = useState('');

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Career Growth',
      description: 'Continuous learning opportunities, mentorship programs, and clear career progression paths.',
    },
    {
      icon: Heart,
      title: 'Work-Life Balance',
      description: 'Flexible work arrangements, generous time-off policies, and wellness programs.',
    },
    {
      icon: Award,
      title: 'Competitive Compensation',
      description: 'Industry-leading salaries, performance bonuses, and comprehensive benefits packages.',
    },
    {
      icon: Users,
      title: 'Collaborative Culture',
      description: 'Inclusive work environment fostering innovation, teamwork, and open communication.',
    },
    {
      icon: GraduationCap,
      title: 'Professional Development',
      description: 'Training programs, conference attendance, and educational assistance for continuing education.',
    },
    {
      icon: Briefcase,
      title: 'Meaningful Work',
      description: 'Contribute to life-saving medicines and make a real impact on global healthcare.',
    },
  ];

  const openPositions = [
    {
      title: 'Senior Research Scientist - Biosimilars',
      department: 'Research & Development',
      location: 'Boston, MA',
      type: 'Full-time',
      experience: '5+ years',
    },
    {
      title: 'Quality Assurance Manager',
      department: 'Quality & Compliance',
      location: 'San Francisco, CA',
      type: 'Full-time',
      experience: '7+ years',
    },
    {
      title: 'Regulatory Affairs Specialist',
      department: 'Regulatory Affairs',
      location: 'Remote',
      type: 'Full-time',
      experience: '3-5 years',
    },
    {
      title: 'Clinical Trial Manager',
      department: 'Clinical Development',
      location: 'New York, NY',
      type: 'Full-time',
      experience: '5+ years',
    },
    {
      title: 'Analytical Chemist',
      department: 'Quality Control',
      location: 'Chicago, IL',
      type: 'Full-time',
      experience: '2-4 years',
    },
    {
      title: 'Pharmacovigilance Associate',
      department: 'Pharmacovigilance',
      location: 'Remote',
      type: 'Full-time',
      experience: '1-3 years',
    },
  ];

  const handleApply = (position) => {
    toast.success(`Application initiated for ${position}`, {
      description: 'You will be redirected to our application portal.',
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-10 lg:py-10">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
              Careers at Apken Pharma
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground font-['Space_Grotesk'] leading-tight">
              Join Our Mission to Improve Lives
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Be part of a team driven by science, quality, and impact. Together, we're advancing healthcare through innovation and excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-10">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <Badge className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-secondary/20">
              Why Apken Pharma
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground font-['Space_Grotesk']">
              Why Join Our Team
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              At Apken Pharma, you'll work alongside talented professionals in a supportive environment that values innovation, collaboration, and personal growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="card-hover border-border">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}